<script setup>
import { ref, computed, onBeforeUnmount, onMounted, nextTick } from 'vue';

const props = defineProps({
  count: { type: Number, required: true },
});
const emit = defineEmits(['exit', 'markUnread', 'mute', 'resolve']);

const menuOpen = ref(false);
const rootRef = ref(null);
const kebabRef = ref(null);
const menuRef = ref(null);
const menuPosition = ref({ top: '0px', left: '0px' });

const label = computed(() => `Selecionados: ${props.count}`);

const positionMenu = () => {
  if (!kebabRef.value) return;

  menuPosition.value = {
    top: '230.3px',
    left: '539.4px',
  };
};

const toggleMenu = async () => {
  menuOpen.value = !menuOpen.value;
  if (menuOpen.value) {
    await nextTick();
    positionMenu();
  }
};
const closeMenu = () => {
  menuOpen.value = false;
};

const onDocClick = e => {
  if (!menuOpen.value) return;
  if (rootRef.value && rootRef.value.contains(e.target)) return;
  if (menuRef.value && menuRef.value.contains(e.target)) return;
  closeMenu();
};
const onScrollOrResize = () => {
  if (menuOpen.value) closeMenu();
};
onMounted(() => {
  document.addEventListener('click', onDocClick);
  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  window.removeEventListener('scroll', onScrollOrResize, true);
  window.removeEventListener('resize', onScrollOrResize);
});

const onAction = action => {
  closeMenu();
  emit(action);
};
</script>

<template>
  <div ref="rootRef" class="acl-sel-toolbar">
    <button
      type="button"
      class="acl-sel-toolbar__btn acl-sel-toolbar__close"
      aria-label="Sair da seleção"
      @click="emit('exit')"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>

    <span class="acl-sel-toolbar__label">{{ label }}</span>

    <div class="acl-sel-toolbar__menu-wrap">
      <button
        ref="kebabRef"
        type="button"
        class="acl-sel-toolbar__btn acl-sel-toolbar__kebab"
        aria-label="Ações em massa"
        @click.stop="toggleMenu"
      >
        <span class="i-lucide-more-vertical size-6" aria-hidden="true" />
      </button>
    </div>
    <Teleport to="body">
      <div
        v-if="menuOpen"
        ref="menuRef"
        class="acl-sel-toolbar__menu"
        :style="menuPosition"
        role="menu"
      >
        <button
          class="acl-sel-toolbar__menu-item"
          type="button"
          :disabled="!count"
          @click="onAction('markUnread')"
        >
          <span
            class="acl-sel-toolbar__menu-icon i-lucide-mail size-4"
            aria-hidden="true"
          />
          Marcar como não lida
        </button>
        <button
          class="acl-sel-toolbar__menu-item"
          type="button"
          :disabled="!count"
          @click="onAction('mute')"
        >
          <span
            class="acl-sel-toolbar__menu-icon i-lucide-bell-off size-4"
            aria-hidden="true"
          />
          Silenciar notificações
        </button>
        <button
          class="acl-sel-toolbar__menu-item"
          type="button"
          :disabled="!count"
          @click="onAction('resolve')"
        >
          <span
            class="acl-sel-toolbar__menu-icon i-lucide-check-check size-4"
            aria-hidden="true"
          />
          Concluir conversas
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.acl-sel-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: #c2bdb826;
  border-bottom: 1px solid #e1e4e8;
  position: relative;
}
.acl-sel-toolbar__btn {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #1a1a1a;
  cursor: pointer;
  transition: background-color 0.15s;
}
.acl-sel-toolbar__btn:hover {
  background-color: rgba(0, 0, 0, 0.06);
}
.acl-sel-toolbar__label {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: left;
}
.acl-sel-toolbar__menu-wrap {
  position: relative;
}
.acl-sel-toolbar__menu {
  position: fixed;
  min-width: 240px;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  z-index: 9999;
  padding: 6px;
}
.acl-sel-toolbar__menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  text-align: left;
  font-size: 15px;
  color: #1a1a1a;
  cursor: pointer;
}
.acl-sel-toolbar__menu-icon {
  width: 16px;
  height: 16px;
  background-color: currentColor;
  flex-shrink: 0;
}
.acl-sel-toolbar__menu-item:hover:not(:disabled) {
  background-color: #f3f4f6;
}
.acl-sel-toolbar__menu-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
:global(.dark) .acl-sel-toolbar__menu {
  background: #1f2123;
  border-color: #2c2f33;
}
:global(.dark) .acl-sel-toolbar__menu-item {
  color: #e5e5e5;
}
:global(.dark) .acl-sel-toolbar__menu-item:hover:not(:disabled) {
  background-color: #2a2d30;
}
</style>
